import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Send, Loader2 } from 'lucide-react';
import { SERVICES, BRAND } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const EMPTY = {
  first_name: '', last_name: '', address: '', phone: '', email: '',
  country: 'Canada', service: '', pickup_address: '', dropoff_address: '',
  consent: false, company_website: '',
};

export default function WebForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [token, setToken] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ['first_name', 'last_name', 'address', 'phone', 'email', 'country', 'service', 'pickup_address', 'dropoff_address'];
    if (required.some((k) => !form[k])) {
      toast({ title: 'Missing details', description: 'Please complete all required fields.' });
      return;
    }
    if (!token) {
      toast({ title: 'Verification required', description: 'Please confirm you are not a robot.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/webform`, { ...form, recaptcha_token: token });
      navigate('/thank-you');
    } catch (err) {
      const desc = err?.response?.data?.detail || ('Please call us at ' + BRAND.phone);
      toast({ title: 'Submission failed', description: desc });
      setToken(null);
      captchaRef.current?.reset();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-7">
      <h2 className="text-xl font-extrabold text-[#4E4E50] mb-1">Book a delivery</h2>
      <p className="text-sm text-gray-500 mb-5">New customer? Fill in the details below and our dispatch team will be in touch.</p>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="first_name">First name *</Label>
            <Input id="first_name" value={form.first_name} onChange={set('first_name')} placeholder="Jane" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name">Last name *</Label>
            <Input id="last_name" value={form.last_name} onChange={set('last_name')} placeholder="Doe" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone number *</Label>
            <Input id="phone" value={form.phone} onChange={set('phone')} placeholder="647-000-0000" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email address *</Label>
            <Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="address">Address *</Label>
          <Input id="address" value={form.address} onChange={set('address')} placeholder="Street, City, Province, Postal Code" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="country">Country *</Label>
            <Input id="country" value={form.country} onChange={set('country')} placeholder="Canada" />
          </div>
          <div className="space-y-1.5">
            <Label>Type of service *</Label>
            <Select value={form.service} onValueChange={set('service')}>
              <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pickup_address">Pickup address (incl. postal code) *</Label>
          <Input id="pickup_address" value={form.pickup_address} onChange={set('pickup_address')} placeholder="Pickup address, City, Postal Code" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dropoff_address">Drop-off address (incl. postal code) *</Label>
          <Input id="dropoff_address" value={form.dropoff_address} onChange={set('dropoff_address')} placeholder="Drop-off address, City, Postal Code" />
        </div>

        {/* Honeypot: hidden from real users */}
        <div className="hidden" aria-hidden="true">
          <label>Company website<input tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set('company_website')} /></label>
        </div>

        {/* Consent */}
        <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
          <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} className="mt-0.5 data-[state=checked]:bg-[#17A2C4] data-[state=checked]:border-[#17A2C4]" />
          <Label htmlFor="consent" className="text-sm text-gray-600 font-normal leading-snug cursor-pointer">
            I agree that StreetFleet may contact me in the future about my delivery, quotes and services.
          </Label>
        </div>

        {/* reCAPTCHA */}
        <div className="flex justify-center">
          {SITE_KEY ? (
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={SITE_KEY}
              onChange={(v) => setToken(v)}
              onExpired={() => setToken(null)}
              onErrored={() => setToken(null)}
            />
          ) : (
            <p className="text-xs text-red-500">reCAPTCHA not configured.</p>
          )}
        </div>

        <Button type="submit" disabled={sending} className="w-full bg-[#17A2C4] hover:bg-[#128aa8] text-white font-bold py-6 gap-2">
          {sending ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : <><Send size={18} /> Submit Webform</>}
        </Button>
        <p className="text-xs text-center text-gray-400">Your request is sent securely to {BRAND.email}.</p>
      </form>
    </div>
  );
}
